import {
    EventInteraction,
    interleaveTwoArrays,
    useTracking,
} from '@eventbrite/ads';
import { PromotedActionsMenuEventCard } from '@eventbrite/ads-event-card-extra';
import { SimpleCarousel } from '@eventbrite/collection-carousel';
import { track } from '@eventbrite/datalayer-library';
import { LoadingCards } from '@eventbrite/eds-event-card';
import { FormattedEvent, ShareUTMOptions } from '@eventbrite/event-renderer';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { trackAnalyticsEvent } from '@eventbrite/site-analytics';
import React from 'react';
import { EventSearchInterfaceName } from '../../../../api/searchAllEvents';
import { OrganicEventSearchFiltersApiPayload } from '../../../../api/searchOrganicEvents';
import { DiscoverVerticalEventCard } from '../../../../components/DiscoverEventCard/DiscoverVerticalEventCard';
import { TrackableComponent } from '../../../../utils/TrackableComponent';
import { CategoryBrowseBucket } from '../../components/CategoryBrowseBucket';
import { CategoryBrowseEmptyState } from '../../components/CategoryBrowseEmptyState';
import { EventLoadingState } from '../../components/CategoryBrowseEventsLoading';
import {
    DEFAULT_SCREEN,
    TAG_TO_DEFAULT_CATEGORY_MAPPING,
    TAG_TO_SCREEN_MAPPING,
} from '../../constants';
import { HEAP_TRACKABLE_EVENT } from '../../constants/heap';
import { useEnvContext, useRouteContext } from '../../context';
import {
    useBaseEventsQueryEvents,
    useBaseSearchQuery,
} from '../../hooks/queries/events';
import { useLocation } from '../../hooks/queries/location';
import { aggregateContext } from '../../utils/analytics';
import { useCategoryBrowsePromotedEvents } from '../CategoryBrowsePromotedEvent';
import { CategoryBrowseViewMore } from '../CategoryBrowseViewMore';
import './CategoryBrowseEvents.scss';

export const VIEW_EVENT_GA_ACTION = 'ViewEvent';
const DEFAULT_TITLE = gettext('Most popular events');

const categoryBrowseUtmOptions: ShareUTMOptions = {
    'utm-campaign': 'social',
    'utm-content': 'attendeeshare',
    'utm-medium': 'discovery',
    'utm-share-source': 'category-browse',
    'utm-term': '',
};

const EventCardSlide: React.FunctionComponent = ({ children }) => {
    return <div className="event-card-simple-carousel-slide">{children}</div>;
};

interface CategoryBrowseEventsProps {
    queryOverload?: OrganicEventSearchFiltersApiPayload;
    renderEmptyState?: boolean;
    lazyLoadCarouselElements?: boolean;
    title?: string | GenericLazyString;
    forceRenderHeader?: boolean;
    bucketName: EventSearchInterfaceName;
    bucketKey: string;
    bucketPosition?: string;
    adSlots?: Array<number>;
    includeHyperlinks?: boolean;
    affCode?: string;
}

export const CategoryBrowseEvents = ({
    queryOverload,
    title = DEFAULT_TITLE,
    adSlots = [], // array with ranks for ads
    bucketKey,
    bucketPosition,
    forceRenderHeader,
    renderEmptyState,
    lazyLoadCarouselElements,
    bucketName,
    includeHyperlinks = true,
    affCode,
}: CategoryBrowseEventsProps) => {
    //Get context data
    const { user, env, request } = useEnvContext();
    const { parsedValues } = useRouteContext();
    const seasonalBucket = parsedValues?.bucket;
    const activeTag = parsedValues?.activeTag || '';
    const isAuthenticated = !!user?.isAuthenticated;
    const locale = env?.localeInfo.locale || 'en_US';

    //Fetch necessary data
    const { isLoading: isLocationLoading } = useLocation();
    const overloadedQuery = useBaseSearchQuery(queryOverload);

    const options = {
        queryOverload: queryOverload,
        refetchOnMount: !queryOverload,
        optOutDedupeEvents: !queryOverload,
        category:
            parsedValues?.category ||
            TAG_TO_DEFAULT_CATEGORY_MAPPING[activeTag] ||
            undefined,
        screen: TAG_TO_SCREEN_MAPPING[activeTag] ?? DEFAULT_SCREEN,
    };

    const { data, isLoading, isFetching, isError, error } =
        useBaseEventsQueryEvents(options);

    const events = data?.events || [];
    const currentBucketHasContent = events && events.length >= 1;

    const {
        promotedEvents = [],
        arePromotedEventsLoading,
        removeEvent,
    } = useCategoryBrowsePromotedEvents({
        search: queryOverload,
        enabled: !isLoading && currentBucketHasContent && !!adSlots.length,
        bucketName,
        slots: adSlots.length,
    });

    const getTrackingContext = ({ id, event }: EventInteraction) =>
        aggregateContext({
            id,
            event,
            bucketKey,
            bucketPosition,
            events: [...(promotedEvents || []), ...events],
            eventSearch: overloadedQuery,
            userId: user?.publicId,
            locale,
            sessionId: request?.session_id,
            guestId: user?.guestId,
            bucketName,
        });
    const { ref } = useTracking<HTMLDivElement>(getTrackingContext);

    const bucketLabel = title?.toString();

    const promotedLoadingCards = adSlots.map((p) => (
        <EventCardSlide key={`promoted-loading-${p}`}>
            <LoadingCards
                style="grid"
                dynamicWidth={true}
                cardCount={1}
                dynamicHeight={true}
            />
        </EventCardSlide>
    ));

    const affiliateCode =
        seasonalBucket && affCode ? affCode : 'ebdssbcategorybrowse';

    const getCommonEventCardProps = (event: FormattedEvent) => ({
        locale,
        statsigLocationString: 'CategoryBrowse - All',
        isAuthenticated,
        event,
        isLazyImage: true,
        onClick: handleOnClick,
        affCode: affiliateCode,
        bucketKey,
        bucketLabel,
        shareOptions: {
            utmOptions: categoryBrowseUtmOptions,
        },
    });

    const promotedEventSlides = promotedEvents.map((promotedEvent) => (
        <EventCardSlide key={`promoted-${promotedEvent.id}`}>
            <DiscoverVerticalEventCard
                {...getCommonEventCardProps(promotedEvent)}
                moreActions={
                    <PromotedActionsMenuEventCard
                        event={promotedEvent}
                        onRemoveEvent={removeEvent}
                        getTrackingContext={getTrackingContext}
                    />
                }
                data-testid="category-browse-promoted-event"
            />
        </EventCardSlide>
    ));

    const organicEventSlides = events?.map((event) => {
        return (
            <EventCardSlide key={event.id}>
                <DiscoverVerticalEventCard
                    {...getCommonEventCardProps(event)}
                    showOrganizer={false}
                    data-testid="category-browse-organic-event"
                />
            </EventCardSlide>
        );
    });
    const allEventSlides = interleaveTwoArrays(
        organicEventSlides,
        arePromotedEventsLoading ? promotedLoadingCards : promotedEventSlides,
        adSlots,
    );

    const handleOnClick = React.useCallback(() => {
        trackAnalyticsEvent({
            category: 'category-page',
            action: VIEW_EVENT_GA_ACTION,
            label: title.toString(),
        });
    }, [title]);

    //Loading state
    const loading = isLocationLoading || isLoading || isFetching;
    if (!forceRenderHeader && loading) {
        return <EventLoadingState />;
    }

    if (!forceRenderHeader && !currentBucketHasContent) {
        if (!renderEmptyState) return null;
        if (isError && error.name === 'AbortError') {
            return (
                <CategoryBrowseEmptyState
                    description={gettext(
                        "Sorry, we're experiencing some issues right now. Try again later.",
                    )}
                />
            );
        }
        return <CategoryBrowseEmptyState />;
    }

    const handleIntersect = () => {
        track({
            eventName: HEAP_TRACKABLE_EVENT,
            eventData: {
                eventBucketLabel: bucketKey,
            },
        });
    };

    // Return event bucket
    return (
        <TrackableComponent onIntersect={handleIntersect}>
            <div
                //@TODO: Create a format util to parse human readable label from tabKey and bucketKey
                data-event-bucket-label={bucketKey?.toString()}
                ref={ref}
            >
                <CategoryBrowseBucket>
                    <SimpleCarousel
                        slidesVisible={{ desktop: 4, tablet: 2 }}
                        headerTitle={
                            includeHyperlinks ? (
                                <CategoryBrowseViewMore
                                    title={title}
                                    queryOverload={queryOverload}
                                >
                                    <h2 className="eds-text-hs">{title}</h2>
                                </CategoryBrowseViewMore>
                            ) : (
                                <h2 className="eds-text-hs">{title}</h2>
                            )
                        }
                        lazilyRenderOffScreenElements={lazyLoadCarouselElements}
                    >
                        {allEventSlides}
                    </SimpleCarousel>
                </CategoryBrowseBucket>
            </div>
        </TrackableComponent>
    );
};
